import { Markdown } from '@davidlloydleisure/common-js-frontend';
import classNames from 'classnames';
import React, { use, useEffect, useState } from 'react';

import { WebsiteLink } from 'src/components/common/website-link';
import TimeCircle from 'src/components/icons/time-circle';
import {
  backgroundAndTextColorForRibbon,
  isPromoEnabledOnPage,
} from 'src/components/layout/redesign/header-shared/promo-helper';
import { SiteContext } from 'src/context/site-context';
import { useClientSnapshot } from 'src/custom-hooks/use-client-snapshot';
import { useRibbonEffects } from 'src/custom-hooks/use-ribbon-effect';
import { amplitudeLinkTrackEvent } from 'src/helpers/amplitude-event-track';
import { getBrandForBuildType } from 'src/helpers/brand-for-build-type';
import { onMarkdownLinkClick } from 'src/helpers/markdown-helper';
import { getSessionStorageItem, setSessionStorageItem } from 'src/helpers/session-storage-helper';
import { TranslatedStaticText } from 'src/translators/static-text-translator';

import * as styles from './promo-ribbon.module.scss';

export type PromoRibbonProps = {
  isMobileNavOpen?: boolean;
  isCollapsed?: boolean;
  isMobile?: boolean;
  onPointerOver?: () => void;
  promoRibbon: Queries.HeaderPromoRibbon;
};

const SESSION_STORAGE_KEY = 'promo-ribbon';

const PromoRibbon = ({
  isMobileNavOpen,
  onPointerOver,
  isCollapsed,
  promoRibbon,
  isMobile,
}: PromoRibbonProps) => {
  const { styling, language, buildType, basePath, pageLevel, isGlobalPage, siteId } =
    use(SiteContext);
  const {
    componentHeight,
    updateComponentHeight,
    shouldHide,
    componentRef,
    textAndChevronContainerRef,
    textRef,
    chevronRef,
  } = useRibbonEffects(0);
  const isHydrated = useClientSnapshot(() => true, false);
  const wasDismissedInPreviousSession = useClientSnapshot(
    () => getSessionStorageItem(SESSION_STORAGE_KEY) === 'close',
    false
  );
  const [hasUserDismissed, setHasUserDismissed] = useState(false);
  const isDismissed = hasUserDismissed || wasDismissedInPreviousSession;

  useEffect(() => {
    updateComponentHeight();
  }, [promoRibbon, updateComponentHeight]);

  if (
    !isPromoEnabledOnPage(
      promoRibbon,
      basePath,
      language,
      buildType,
      isGlobalPage ? undefined : siteId
    ) ||
    !promoRibbon?.text
  ) {
    return null;
  }

  const isIconVisible = !!promoRibbon?.enabledCrossIcon;
  const backgroundAndTextColorStyle = backgroundAndTextColorForRibbon(styling, promoRibbon.color);
  const shouldHideComponent = shouldHide || isCollapsed || !isHydrated || isDismissed;
  const hiddenAtMobile = isMobileNavOpen || shouldHideComponent;
  const hiddenAtDesktop = shouldHideComponent;

  const wrapperStyle = {
    '--ribbon-bg': backgroundAndTextColorStyle.backgroundColor,
    '--ribbon-color': backgroundAndTextColorStyle.color,
    '--ribbon-height-mobile': hiddenAtMobile ? '0' : `${componentHeight}px`,
    '--ribbon-height-desktop': hiddenAtDesktop ? '0' : `${componentHeight}px`,
    '--ribbon-visibility-mobile': hiddenAtMobile ? 'hidden' : 'visible',
    '--ribbon-visibility-desktop': hiddenAtDesktop ? 'hidden' : 'visible',
    '--ribbon-transition-mobile': hiddenAtMobile
      ? 'height 0.4s ease, visibility 0.4s'
      : 'height 0.4s ease',
    '--ribbon-transition-desktop': hiddenAtDesktop
      ? 'height 0.4s ease, visibility 0.4s'
      : 'height 0.4s ease',
  } as React.CSSProperties;

  const handleClick = () => {
    void amplitudeLinkTrackEvent({
      buildType: buildType,
      language,
      linkPath: promoRibbon.link,
      linkName: promoRibbon.text,
      componentName: 'Promo Ribbon',
      pageLevel,
    });
  };

  const onClick = () => {
    setHasUserDismissed(true);
    setSessionStorageItem(SESSION_STORAGE_KEY, 'close');
  };
  const brand = getBrandForBuildType(buildType);

  return (
    <div className={styles.openCloseWrapper} style={wrapperStyle} onPointerOver={onPointerOver}>
      <div
        className={styles.wrapper}
        ref={componentRef}
        data-testid={`promo-ribbon-${componentHeight}`}
      >
        <div
          className={classNames(
            styles.container,
            isIconVisible && styles.containerIcon,
            isMobile && styles.containerMobile
          )}
          ref={textAndChevronContainerRef}
        >
          <div className={styles.textWrapper} ref={textRef}>
            {promoRibbon.link ? (
              <WebsiteLink to={promoRibbon.link} className={styles.link} handleClick={handleClick}>
                <Markdown
                  markdownText={promoRibbon.text}
                  brand={brand}
                  colorOverride={backgroundAndTextColorStyle.color}
                  onLinkClick={onMarkdownLinkClick(buildType, language, 'Promo Ribbon')}
                  classNameToOverride={classNames(styles.promoMarkdown)}
                />
              </WebsiteLink>
            ) : (
              <Markdown
                markdownText={promoRibbon.text}
                brand={brand}
                colorOverride={backgroundAndTextColorStyle.color}
                onLinkClick={onMarkdownLinkClick(buildType, language, 'Promo Ribbon')}
                classNameToOverride={classNames(styles.promoMarkdown)}
              />
            )}
          </div>
          {isIconVisible && (
            <div
              ref={chevronRef}
              tabIndex={0}
              className={classNames(styles.closeWrapper, isMobile && styles.closeWrapperMobile)}
              onClick={onClick}
            >
              {!isMobile && (
                <span className={styles.removeText}>
                  <TranslatedStaticText lookupKey="remove" />
                </span>
              )}
              <TimeCircle fill={backgroundAndTextColorStyle.color} size="1rem" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoRibbon;
